import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, ArrowRight, ShieldCheck } from "lucide-react";

export default function Signup() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "", slug: "", admin_full_name: "",
    admin_email: "", admin_password: "", plan_code: "trial", accept_terms: false,
  });
  const [slugStatus, setSlugStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${api}/api/public/pricing`).then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.slug || form.slug.length < 3) { setSlugStatus(null); return; }
    const t = setTimeout(async () => {
      try {
        const api = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${api}/api/public/check-slug/${form.slug}`);
        setSlugStatus(data);
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [form.slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) return toast.error("Please accept terms");
    setSubmitting(true);
    try {
      const api = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(`${api}/api/public/signup`, form);
      toast.success("Workspace created! Redirecting to login…");
      setTimeout(() => nav("/login"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 qh-gradient-soft" aria-hidden="true" />
      <div className="relative max-w-5xl mx-auto p-6 sm:p-12">
        <div className="flex items-center gap-3 mb-8">
          <img src="/qrewhr-logo.jpg" alt="Qrew HR" className="w-11 h-11 rounded-xl" />
          <div>
            <div className="text-lg font-bold">Qrew HR</div>
            <div className="text-[11px] text-slate-500">Smart HR. Stronger Teams.</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-8 qh-ring">
            <h1 className="text-3xl font-bold tracking-tight">Start your free 14-day trial</h1>
            <p className="text-sm text-slate-500 mt-1">No credit card. Up to 5 employees. Full feature access.</p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Company name *</Label>
                  <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 h-11" data-testid="signup-company" />
                </div>
                <div>
                  <Label className="text-xs">Workspace slug *</Label>
                  <div className="flex items-center mt-1">
                    <Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} className="h-11 rounded-r-none" placeholder="acme" data-testid="signup-slug" />
                    <span className="h-11 px-3 flex items-center bg-slate-100 border border-l-0 rounded-r-md text-xs text-slate-500">.qrewhr.com</span>
                  </div>
                  {slugStatus && <p className={`text-[11px] mt-1 ${slugStatus.available ? "text-emerald-600" : "text-rose-600"}`}>{slugStatus.reason}</p>}
                </div>
                <div>
                  <Label className="text-xs">Your full name *</Label>
                  <Input required value={form.admin_full_name} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} className="mt-1 h-11" data-testid="signup-name" />
                </div>
                <div>
                  <Label className="text-xs">Work email *</Label>
                  <Input type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1 h-11" data-testid="signup-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Password (min 8 chars) *</Label>
                  <Input type="password" required minLength={8} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="mt-1 h-11" data-testid="signup-password" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: v })} data-testid="signup-accept" />
                <span className="text-xs text-slate-600">I agree to the <a className="text-violet-700 hover:underline" href="#">Terms of Service</a> and <a className="text-violet-700 hover:underline" href="#">Privacy Policy</a>.</span>
              </div>
              <Button type="submit" disabled={submitting || (slugStatus && !slugStatus.available)} className="w-full h-11 qh-gradient text-white text-base" data-testid="signup-submit">
                {submitting ? "Creating workspace…" : <>Create my workspace <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              <p className="text-center text-xs text-slate-500">Already have a workspace? <Link to="/login" className="text-violet-700 hover:underline">Sign in</Link></p>
            </form>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 shadow-md">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Your trial includes</div>
              {[
                "All HR modules unlocked",
                "Up to 5 employees",
                "AI Assistant (Claude 4.5)",
                "Tasks · Attendance · Leaves",
                "Branded payslips & PDFs",
                "Email + browser notifications",
              ].map((p) => (
                <div key={p} className="flex items-start gap-2 py-1.5"><Check className="w-4 h-4 text-emerald-500 flex-none mt-0.5" /><span className="text-sm text-slate-700">{p}</span></div>
              ))}
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-5 border border-violet-100">
              <ShieldCheck className="w-5 h-5 text-violet-700 mb-2" />
              <div className="text-sm font-semibold">Tenant-isolated by design</div>
              <p className="text-[11px] text-slate-600 mt-1">Your data lives in a dedicated tenant scope. JWT context-vars enforce isolation on every query.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
